import { useEffect, useState } from 'react';
import { Auth } from 'app/scripts/db/Auth';
import type {
  Board,
  Plugin,
  PluginBoardButton,
} from './PluginHeaderButton.types';
import { usePluginData } from './usePluginData';
import {
  mapPowerUpId as MAP_POWER_UP_ID,
  customFieldsId as CUSTOM_FIELDS_POWER_UP_ID,
} from '@trello/config';

export const usePluginBoardButtons = (
  board: Board,
  hiddenPluginBoardButtons?: string[],
) => {
  const [pluginBoardButtons, setPluginBoardButtons] = useState<
    PluginBoardButton[] | undefined
  >([]);

  const { isLoading, pluginsData } = usePluginData(
    board,
    'board-buttons',
    hiddenPluginBoardButtons,
  );
  const plugins = pluginsData?.plugins;

  useEffect(() => {
    const filteredPluginButtons = pluginsData?.commandData
      ?.map((pluginData) => {
        return pluginData as PluginBoardButton;
      })
      .filter((pluginButton: PluginBoardButton) => {
        if (
          Array.isArray(pluginButton) ||
          !(pluginButton instanceof Object) ||
          !pluginButton.text
        ) {
          return false;
        }

        if (
          pluginButton.condition === null ||
          pluginButton.condition === undefined
        ) {
          return board.editable();
        }

        switch (pluginButton.condition) {
          case 'admin':
            return board.owned();
          case 'edit':
            return board.editable();
          case 'readOnly':
            return !board.editable();
          case 'signedIn':
            return Auth.isLoggedIn();
          case 'signedOut':
            return !Auth.isLoggedIn();
          case 'always':
            return true;
          default:
            return false;
        }
      });

    setPluginBoardButtons(filteredPluginButtons);
  }, [board, pluginsData?.commandData]);

  const groupedBoardButtons = pluginBoardButtons?.reduce(
    (map, pluginBoardButton) => {
      const { idPlugin } = pluginBoardButton;
      return {
        ...map,
        [idPlugin]: [...(map[idPlugin] || []), pluginBoardButton],
      };
    },
    {} as Record<string, PluginBoardButton[]>,
  );

  const pluginsById = plugins?.reduce((obj, plugin) => {
    //Remove custom fields from pluginlist always because it is now core feature
    //Remove map pup from list if it is a core feature for the board.
    if (
      !(
        plugin.id === CUSTOM_FIELDS_POWER_UP_ID ||
        (plugin.id === MAP_POWER_UP_ID && board.isMapCore())
      )
    ) {
      return Object.assign(obj, { [plugin.id]: plugin });
    } else {
      return obj;
    }
  }, {} as Record<string, Plugin>);

  if (!groupedBoardButtons || !pluginsById) {
    return {
      isLoading: isLoading,
      pluginsData: undefined,
      error: 'boardPlugins are undefined',
    };
  }

  return {
    isLoading: isLoading,
    pluginsData: {
      numPlugins: pluginsData?.plugins?.length,
      groupedBoardButtons: groupedBoardButtons,
      pluginsById: pluginsById,
    },
    error: '',
  };
};
